import type { OpticalMeasurePointAdjustmentState } from '../../index'
import type {
  MeasurementFlowPhase,
  PdFlowViewState,
  PdMeasurementResult,
  ShFlowViewState,
  ShMeasurementResult,
} from '../types'
import { derivePdViewState, deriveShViewState } from './deriveViewState'

export type MeasurementFlowDebugScenario<ViewState> = {
  key: string
  label: string
  viewState: ViewState
  /**
   * Stands in for the flow's lens height while the scenario is active. The
   * frame-range warning is derived from the vbox as well as the result, so a
   * scenario that has to show it cannot do so from `viewState` alone.
   */
  vboxMm?: number
}

type GuidingScenario = {
  key: string
  label: string
  phase: MeasurementFlowPhase
  progress?: number
}

const MOCK_SH_RESULT: ShMeasurementResult = { left: 21.4, right: 18.6 }

/** Both eyes above the 15–26 mm window a 36 mm lens height allows. */
const MOCK_SH_OUT_OF_RANGE_VBOX_MM = 36
const MOCK_SH_OUT_OF_RANGE_RESULT: ShMeasurementResult = {
  left: 28.4,
  right: 27.9,
}

const MOCK_PD_RESULT: PdMeasurementResult = {
  pd: 63.4,
  leftPd: 31.9,
  rightPd: 31.5,
  nearPd: 60.6,
  nearLeftPd: 30.5,
  nearRightPd: 30.1,
}

const MOCK_SH_ADJUSTMENT: OpticalMeasurePointAdjustmentState = {
  active: true,
  type: 'sh-bottom',
  points: { left: { x: 312, y: 548 }, right: { x: 468, y: 552 } },
  originalPoints: { left: { x: 312, y: 548 }, right: { x: 468, y: 552 } },
  scale: { leftMmPerPixel: 0.082, rightMmPerPixel: 0.081 },
}

// Non-zero progress on warning states is deliberate: progress persists across
// user corrections (SH keeps its captured frames natively), so the bar should
// stay visible mid-scan. Only calibrating (initializing) starts at zero.
const SH_GUIDING_SCENARIOS: GuidingScenario[] = [
  { key: 'calibrating', label: 'Calibrating', phase: 'calibrating' },
  {
    key: 'detecting',
    label: 'Detecting Face',
    phase: 'detecting',
    progress: 0.2,
  },
  { key: 'tooClose', label: 'Too Close', phase: 'tooClose', progress: 0.3 },
  { key: 'tooFar', label: 'Too Far', phase: 'tooFar', progress: 0.35 },
  {
    key: 'positioning',
    label: 'Positioning',
    phase: 'positioning',
    progress: 0.4,
  },
  { key: 'opening', label: 'Eyes Closed', phase: 'opening', progress: 0.45 },
  {
    key: 'noGlasses',
    label: 'No Glasses',
    phase: 'noGlasses',
    progress: 0.5,
  },
  {
    key: 'findingFrame',
    label: 'Finding Frame',
    phase: 'findingFrame',
    progress: 0.5,
  },
  { key: 'sampling', label: 'Sampling 60%', phase: 'sampling', progress: 0.6 },
]

const PD_GUIDING_SCENARIOS: GuidingScenario[] = [
  {
    key: 'detecting',
    label: 'Detecting Face',
    phase: 'detecting',
    progress: 0.2,
  },
  { key: 'tooClose', label: 'Too Close', phase: 'tooClose', progress: 0.3 },
  { key: 'tooFar', label: 'Too Far', phase: 'tooFar', progress: 0.35 },
  {
    key: 'positioning',
    label: 'Positioning',
    phase: 'positioning',
    progress: 0.4,
  },
  { key: 'opening', label: 'Eyes Closed', phase: 'opening', progress: 0.45 },
  {
    key: 'stabilising',
    label: 'Stabilising',
    phase: 'stabilising',
    progress: 0.5,
  },
  { key: 'sampling', label: 'Sampling 60%', phase: 'sampling', progress: 0.6 },
]

export const buildShDebugScenarios =
  (): MeasurementFlowDebugScenario<ShFlowViewState>[] => [
    ...SH_GUIDING_SCENARIOS.map(({ key, label, phase, progress }) => ({
      key,
      label,
      viewState: deriveShViewState({
        phase,
        progress: progress ?? 0,
        result: null,
      }),
    })),
    {
      key: 'processing',
      label: 'Processing',
      viewState: deriveShViewState({
        phase: 'complete',
        progress: 1,
        result: null,
      }),
    },
    {
      key: 'review',
      label: 'Review Result',
      viewState: deriveShViewState({
        phase: 'complete',
        progress: 1,
        result: MOCK_SH_RESULT,
      }),
    },
    {
      key: 'reviewOutOfRange',
      label: 'Review + Out of Range',
      vboxMm: MOCK_SH_OUT_OF_RANGE_VBOX_MM,
      viewState: deriveShViewState({
        phase: 'complete',
        progress: 1,
        result: MOCK_SH_OUT_OF_RANGE_RESULT,
      }),
    },
    {
      key: 'reviewAdjust',
      label: 'Review + Adjust',
      viewState: deriveShViewState({
        phase: 'complete',
        progress: 1,
        result: MOCK_SH_RESULT,
        adjustment: MOCK_SH_ADJUSTMENT,
      }),
    },
    {
      key: 'failed',
      label: 'Failed',
      viewState: deriveShViewState({
        phase: 'failed',
        progress: 0,
        result: null,
      }),
    },
  ]

export const buildPdDebugScenarios =
  (): MeasurementFlowDebugScenario<PdFlowViewState>[] => [
    ...PD_GUIDING_SCENARIOS.map(({ key, label, phase, progress }) => ({
      key,
      label,
      viewState: derivePdViewState({
        phase,
        progress: progress ?? 0,
        result: null,
      }),
    })),
    {
      key: 'review',
      label: 'Review Result',
      viewState: derivePdViewState({
        phase: 'complete',
        progress: 1,
        result: MOCK_PD_RESULT,
      }),
    },
    {
      key: 'failed',
      label: 'Failed',
      viewState: derivePdViewState({
        phase: 'failed',
        progress: 0,
        result: null,
      }),
    },
  ]
